/**
 * ToolsGrid - Responsive grid of all tools (Explorar section)
 * Replaces carousel with scalable grid layout
 * Active tools first, then coming soon/locked with discrete styling
 */

import React from 'react';
import { tools, getToolStatus } from '@/data/tools';
import ToolCard, { EffectiveToolStatus } from '@/components/ToolCard';
import { useRole, UserRole } from '@/contexts/RoleContext';
import { ToolSetting, getEffectiveToolStatus } from '@/services/toolSettings';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ToolsGridProps {
  favoriteIds?: string[];
  onToggleFavorite?: (toolId: string) => void;
  showFavoriteButton?: boolean;
  /** @deprecated Animations removed for instant loading */
  skipAnimation?: boolean;
  getSettingByToolId?: (toolId: string) => ToolSetting | undefined;
  excludeIds?: string[];
}

export default function ToolsGrid({ 
  favoriteIds = [], 
  onToggleFavorite,
  showFavoriteButton = false,
  getSettingByToolId,
  excludeIds = [],
}: ToolsGridProps) {
  // CRITICAL: Do not render until role is loaded to prevent flicker
  // Parent component (Index) should gate rendering, but this is a safety check
  const { effectiveRole } = useRole();

  // Helper to get effective status for a tool - includes staticAllowedRoles for proper fallback
  const getToolEffectiveStatus = (toolId: string, staticStatus: 'active' | 'coming_soon' | 'locked', staticAllowedRoles: readonly UserRole[]): EffectiveToolStatus => {
    const dbSetting = getSettingByToolId?.(toolId);
    return getEffectiveToolStatus(toolId, effectiveRole, dbSetting, staticStatus, staticAllowedRoles);
  };

  // Filter and sort tools
  // 1. Exclude fixed tools and excluded IDs
  // 2. Favorites first
  // 3. Active first, then locked/coming_soon
  // 4. Alphabetical within same status
  const sortedTools = tools
    .filter(tool => !tool.isFixed && !tool.isUiOnly && !excludeIds.includes(tool.id))
    .map(tool => {
      const staticStatus = getToolStatus(tool, effectiveRole);
      // CRITICAL: Pass allowedRoles for proper role validation fallback
      const effectiveStatus = getToolEffectiveStatus(tool.id, staticStatus, tool.allowedRoles);
      const isFavorite = favoriteIds.includes(tool.id);
      return { tool, effectiveStatus, isFavorite };
    })
    .sort((a, b) => {
      // Favorites first
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      
      // Active tools first
      const aActive = a.effectiveStatus.status === 'active';
      const bActive = b.effectiveStatus.status === 'active';
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;
      
      // Alphabetical
      return a.tool.name.localeCompare(b.tool.name, 'pt-BR');
    });

  if (sortedTools.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        Nenhuma ferramenta disponível.
      </div>
    );
  }

  // Calculate ghost cards needed (fill up to 4 minimum)
  const ghostCardsCount = sortedTools.length < 4 ? Math.min(2, 4 - sortedTools.length) : 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
      {sortedTools.map(({ tool, effectiveStatus, isFavorite }, index) => (
        <ToolCard 
          key={tool.id}
          tool={tool} 
          index={index}
          isFavorite={isFavorite}
          onToggleFavorite={() => onToggleFavorite?.(tool.id)}
          showFavoriteButton={showFavoriteButton}
          skipAnimation
          backgroundImageUrl={getSettingByToolId?.(tool.id)?.background_image_url}
          effectiveStatus={effectiveStatus}
        />
      ))}
      
      {/* Ghost cards to fill empty space */}
      {Array.from({ length: ghostCardsCount }).map((_, i) => (
        <div 
          key={`ghost-${i}`}
          className={cn(
            "relative overflow-hidden rounded-2xl p-4 h-[140px]",
            "bg-gradient-to-br from-card/30 via-card/20 to-transparent",
            "border border-dashed border-border/40",
            "flex flex-col items-center justify-center gap-2",
            "opacity-50 cursor-default select-none"
          )}
        >
          <div className="w-10 h-10 rounded-xl bg-secondary/30 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-muted-foreground/50" />
          </div>
          <span className="text-[10px] text-muted-foreground/60 font-medium">
            Em breve
          </span>
        </div>
      ))}
    </div>
  );
}
